/**
 * 批量事务抽屉 V4.0 - 事务表格组件
 * 显示航班事务列表，支持嵌套明细表格
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Table, Button, Space, Tooltip, message, Card, Tag, Select, Divider, Input, Modal } from "antd";
import { ExpandAltOutlined, ShrinkOutlined, EyeOutlined, CaretDownOutlined, CaretRightOutlined, RightOutlined, PlusOutlined, ShopOutlined, ThunderboltOutlined, EditOutlined } from "@ant-design/icons";
import { useBatchTransactionContext } from "../context/BatchTransactionContext";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";
import TransactionDetailTable from "./TransactionDetailTable";
import ProductSelectorModal from "../../../../components/common/ProductSelectorModal";
import EnhancedFlightTimes from "../../Monitor/components/EnhancedFlightTimes";
import { useBatchTransactionAPI } from "../hooks/useBatchTransactionAPI";
import UnifiedStatusEditor from "./UnifiedStatusEditor";
import { CLIENT_STATUS, DATA_LOADING_STATUS } from "../types";
import { filterFlightsByAirports } from "../utils";
import { getTransactionStatusOptions } from "../../Monitor/constants/transactionConstants";

/**
 * 事务表格组件 - V5.0 重构版本
 */
const TransactionTable = ({ onViewFlightHistory, selectedFlightForHistory }) => {
    const {
        // 状态
        state,
        // 事务管理器方法
        transactionManager,
        // 编排器状态
        orchestrator,
    } = useBatchTransactionContext();

    const { selectedFlights, transactionType, activeAirports, selectedSupplier, transactions, dataLoadingStatus, availableProducts } = state;
    const { airportsMap } = useTimeZone();

    const [productModalVisible, setProductModalVisible] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [allExpanded, setAllExpanded] = useState(true);
    const [currentTransactionForProduct, setCurrentTransactionForProduct] = useState(null);
    const [modalAvailableProducts, setModalAvailableProducts] = useState([]);

    const { getAvailableProductsByAirports } = useBatchTransactionAPI();

    // 同步UI与State：确保所有应显示的航班都有对应的事务对象存在于中央状态中
    useEffect(() => {
        // 如果没有选择机场，则不执行任何操作
        if (!activeAirports || activeAirports.length === 0) {
            return;
        }

        // 1. 找出所有当前条件下应该在UI中显示的航班
        const visibleFlights = filterFlightsByAirports(selectedFlights, activeAirports, transactionType);

        // 2. 获取当前状态中已存在的事务对应的航班ID集合
        const existingTransactionFlightIds = new Set(transactions.map((t) => t.flightId));

        // 3. 找出那些应该显示、但在状态中还没有对应事务的航班
        const flightsMissingTransaction = visibleFlights.filter((f) => !existingTransactionFlightIds.has(f.id));

        // 4. 如果存在缺失的事务，则调用manager创建它们
        if (flightsMissingTransaction.length > 0) {
            console.log(
                "🔍 检测到需要创建事务壳的航班:",
                flightsMissingTransaction.map((f) => f.flight_number),
            );
            transactionManager.createTransactionShells(flightsMissingTransaction);
        }
    }, [selectedFlights, activeAirports, transactions, transactionManager]);

    // 获取当前事务类型的状态选项
    const transactionStatusOptions = useMemo(() => getTransactionStatusOptions(transactionType), [transactionType]);

    // 设置默认展开状态
    React.useEffect(() => {
        if (transactions.length > 0) {
            setExpandedRowKeys(transactions.map((t) => t.id));
        }
    }, [transactions]);

    // 处理明细数量点击展开/收缩
    const handleDetailsCountClick = useCallback(
        (record) => {
            const isExpanded = expandedRowKeys.includes(record.id);
            if (isExpanded) {
                // 收缩
                setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
            } else {
                // 展开
                setExpandedRowKeys((prev) => [...prev, record.id]);
            }
        },
        [expandedRowKeys],
    );

    // 构建显示用的事务数据
    const displayTransactions = useMemo(() => {
        // 在数据加载期间或未选择任何机场时，不显示任何事务
        if (dataLoadingStatus === DATA_LOADING_STATUS.LOADING || !activeAirports || activeAirports.length === 0) {
            return [];
        }

        // 1. 将 `state.transactions` 作为唯一数据源
        const filteredTransactions = transactions.filter((transaction) => {
            const flight = selectedFlights.find((f) => f.id === transaction.flightId);
            if (!flight) {
                // 如果在航班列表中找不到对应的航班，则不显示该事务
                return false;
            }
            // 根据当前激活的机场进行筛选
            // 对于配餐/燃油订单，只匹配始发机场；其他事务类型匹配始发或目的地机场
            if (transactionType === "CATERING_ORDER" || transactionType === "FUEL_REQUEST") {
                return activeAirports.includes(flight.departure_airport);
            } else {
                return activeAirports.includes(flight.departure_airport) || activeAirports.includes(flight.arrival_airport);
            }
        });

        // 2. 为筛选后的事务补充用于UI展示的航班信息
        // 注意：优先使用事务中已经处理好的快照数据，而不是实时航班数据
        const enrichedTransactions = filteredTransactions.map((transaction) => {
            const originalFlightData = selectedFlights.find((f) => f.id === transaction.flightId);
            // `originalFlightData` 此时一定存在，因为上面已经过滤掉了不存在的

            // 优先使用事务中的快照数据（如果存在），否则使用实时航班数据
            // 这些数据在 TransactionDataContext 中已经正确处理过了
            return {
                ...transaction,
                // 使用事务中已经处理好的航班信息（优先快照数据）
                flightNumber: transaction.flightNumber || originalFlightData.flight_number,
                flightDate: transaction.flightDate || originalFlightData.flight_date,
                route: transaction.route || `${originalFlightData.departure_airport}-${originalFlightData.arrival_airport}`,
                std: transaction.std || originalFlightData.std,
                sta: transaction.sta || originalFlightData.sta,
                etd: transaction.etd || originalFlightData.etd,
                eta: transaction.eta || originalFlightData.eta,
                atd: transaction.atd || originalFlightData.atd,
                ata: transaction.ata || originalFlightData.ata,
                flightStatus: originalFlightData.status, // 航班状态始终使用实时数据

                // ⚠️ 重要：传递 GT 和 BH 相关字段（之前遗漏了这些字段）
                min_ground_time_minutes: originalFlightData.min_ground_time_minutes,
                actual_ground_time_minutes: originalFlightData.actual_ground_time_minutes,
                block_time_minutes: originalFlightData.block_time_minutes,

                // 其他属性
                details_count: transaction.details.filter((d) => d.clientStatus !== CLIENT_STATUS.DELETED).length,
                isNewTransaction: typeof transaction.id === "string",

                // 快照相关信息传递给表格渲染
                hasFlightSnapshot: transaction.hasFlightSnapshot,
                flightDataSource: transaction.flightDataSource,
                useSnapshotData: transaction.useSnapshotData,
            };
        });

        // 3. 按计划起飞时间（STD）对所有事务进行排序
        return enrichedTransactions.sort((a, b) => {
            if (!a.std) return 1; // 没有std的排在后面
            if (!b.std) return -1;
            return a.std.localeCompare(b.std);
        });
    }, [transactions, selectedFlights, activeAirports, dataLoadingStatus]);

    // 全部展开/收缩
    const handleToggleAllExpanded = useCallback(() => {
        if (allExpanded) {
            setExpandedRowKeys([]);
        } else {
            setExpandedRowKeys(displayTransactions.map((t) => t.id));
        }
        setAllExpanded(!allExpanded);
    }, [allExpanded, displayTransactions]);

    // 批量修改产品（覆盖所有事务明细）
    const handleBatchEditProducts = useCallback(async () => {
        if (!selectedSupplier) {
            message.warning("请先选择供应商");
            return;
        }
        if (!activeAirports?.length) {
            message.warning("请先选择机场");
            return;
        }
        try {
            const products = await getAvailableProductsByAirports(transactionType, activeAirports);
            setModalAvailableProducts(products || []);
            setCurrentTransactionForProduct(null); // 批量模式
            setProductModalVisible(true);
        } catch (e) {
            message.error("加载可用产品失败");
        }
    }, [selectedSupplier, activeAirports, transactionType, getAvailableProductsByAirports]);

    // 添加产品明细（单事务）
    const handleAddProduct = useCallback(
        (transaction) => {
            if (!selectedSupplier) {
                message.warning("请先选择供应商");
                return;
            }
            setCurrentTransactionForProduct(transaction);
            // 修复：从全局可用产品中筛选当前供应商的产品，而不是使用 selectedContractItems
            const supplierProducts = (availableProducts || []).filter((p) => p.partner_id === selectedSupplier.id);
            if (supplierProducts.length === 0) {
                message.warning("当前供应商没有可用的产品。");
                return;
            }
            setModalAvailableProducts(supplierProducts);
            setProductModalVisible(true);
        },
        [selectedSupplier, availableProducts],
    );

    // 处理产品选择确认
    const handleProductSelect = useCallback(
        (selectedProducts) => {
            if (!selectedProducts.length) return;

            if (currentTransactionForProduct) {
                // 单事务添加 - 使用增强版函数自动处理许可类型
                selectedProducts.forEach((product) => {
                    console.log("🔍 手动添加明细到事务:", {
                        transactionId: currentTransactionForProduct.id,
                        product: product,
                        tags: product.tags,
                        transactionType: transactionType,
                    });
                    transactionManager.addDetailWithPermitType(currentTransactionForProduct.id, product);
                });
                message.success(`已为事务添加 ${selectedProducts.length} 个产品明细`);
            } else {
                // 批量智能添加：只添加新产品，跳过已有的产品
                let totalAdded = 0;
                let totalSkipped = 0;

                transactions.forEach((t) => {
                    // 获取当前事务已有的产品ID列表
                    const existingProductIds = new Set(t.details.filter((detail) => detail.clientStatus !== CLIENT_STATUS.DELETED).map((detail) => detail.contractItemId));

                    console.log("🔍 检查事务现有产品:", {
                        transactionId: t.id,
                        flightNumber: t.flightNumber,
                        totalDetails: t.details.length,
                        activeDetails: t.details.filter((detail) => detail.clientStatus !== CLIENT_STATUS.DELETED).length,
                        existingProductIds: Array.from(existingProductIds),
                        allDetails: t.details.map((d) => ({
                            id: d.id,
                            contractItemId: d.contractItemId,
                            productName: d.productName,
                            clientStatus: d.clientStatus,
                        })),
                    });

                    // 只添加不存在的产品
                    selectedProducts.forEach((product) => {
                        if (existingProductIds.has(product.id)) {
                            // 产品已存在，跳过
                            totalSkipped++;
                            console.log("🔍 批量添加跳过已有产品:", {
                                transactionId: t.id,
                                productId: product.id,
                                productName: product.product_name,
                                reason: "产品已存在于明细中",
                            });
                        } else {
                            // 产品不存在，添加新明细
                            totalAdded++;
                            console.log("🔍 批量添加新产品明细:", {
                                transactionId: t.id,
                                product: product,
                                tags: product.tags,
                                transactionType: transactionType,
                            });
                            transactionManager.addDetailWithPermitType(t.id, product);
                        }
                    });
                });

                // 显示操作结果
                const resultMessages = [];
                if (totalAdded > 0) {
                    resultMessages.push(`新增 ${totalAdded} 个产品明细`);
                }
                if (totalSkipped > 0) {
                    resultMessages.push(`跳过 ${totalSkipped} 个已有产品`);
                }

                if (resultMessages.length > 0) {
                    message.success(`批量操作完成：${resultMessages.join("，")}`);
                } else {
                    message.info("所有选择的产品都已存在于事务明细中");
                }
            }

            setProductModalVisible(false);
            setCurrentTransactionForProduct(null);
        },
        [currentTransactionForProduct, transactionManager, transactions, transactionType],
    );

    // 查看航班历史
    const handleViewHistory = useCallback(
        (record) => {
            const flight = selectedFlights.find((f) => f.id === record.flightId);
            if (flight && onViewFlightHistory) {
                onViewFlightHistory(flight);
            }
        },
        [selectedFlights, onViewFlightHistory],
    );

    // 批量设置状态
    const handleBatchStatusChange = useCallback(
        async (newStatus) => {
            if (!newStatus) return;

            try {
                let updatedTransactionCount = 0;
                let updatedDetailCount = 0;

                // 遍历所有事务
                for (const transaction of displayTransactions) {
                    // 更新事务状态
                    await transactionManager.updateTransaction(transaction.id, { status: newStatus });
                    updatedTransactionCount++;

                    // 对于许可类事务，同时更新所有明细的状态
                    if (transactionType === "PERMIT_REQUEST" && transaction.details && transaction.details.length > 0) {
                        for (const detail of transaction.details) {
                            // 只更新未删除的明细
                            if (detail.clientStatus !== CLIENT_STATUS.DELETED) {
                                await transactionManager.updateDetail(transaction.id, detail.id, { currentStatus: newStatus });
                                updatedDetailCount++;
                            }
                        }
                    }
                }

                message.success(`批量状态更新完成：${updatedTransactionCount} 个事务${updatedDetailCount > 0 ? `，${updatedDetailCount} 个明细` : ""}`);
            } catch (error) {
                console.error("批量状态更新失败:", error);
                message.error("批量状态更新失败: " + (error.message || "未知错误"));
            }
        },
        [displayTransactions, transactionType, transactionManager],
    );

    // 批量修改备注
    const [batchRemarksModalVisible, setBatchRemarksModalVisible] = useState(false);
    const [batchRemarksValue, setBatchRemarksValue] = useState("");
    const [batchRemarksLoading, setBatchRemarksLoading] = useState(false);

    const handleBatchRemarksClick = useCallback(() => {
        setBatchRemarksValue("");
        setBatchRemarksModalVisible(true);
    }, []);

    const handleBatchRemarksCancel = useCallback(() => {
        setBatchRemarksModalVisible(false);
        setBatchRemarksValue("");
    }, []);

    const handleBatchRemarksConfirm = useCallback(async () => {
        try {
            setBatchRemarksLoading(true);
            let updatedTransactionCount = 0;
            let updatedDetailCount = 0;

            // 遍历所有事务
            for (const transaction of displayTransactions) {
                // 追加事务备注
                const currentTransactionRemarks = transaction.remarks || "";
                const newTransactionRemarks = currentTransactionRemarks ? `${currentTransactionRemarks}\n${batchRemarksValue}` : batchRemarksValue;
                await transactionManager.updateTransaction(transaction.id, { remarks: newTransactionRemarks });
                updatedTransactionCount++;

                // 追加所有明细的备注
                if (transaction.details && transaction.details.length > 0) {
                    for (const detail of transaction.details) {
                        // 只更新未删除的明细
                        if (detail.clientStatus !== CLIENT_STATUS.DELETED) {
                            const currentDetailRemarks = detail.remarks || "";
                            const newDetailRemarks = currentDetailRemarks ? `${currentDetailRemarks}\n${batchRemarksValue}` : batchRemarksValue;
                            await transactionManager.updateDetail(transaction.id, detail.id, { remarks: newDetailRemarks });
                            updatedDetailCount++;
                        }
                    }
                }
            }

            message.success(`批量备注追加完成：${updatedTransactionCount} 个事务，${updatedDetailCount} 个明细`);
            setBatchRemarksModalVisible(false);
            setBatchRemarksValue("");
        } catch (error) {
            console.error("批量备注追加失败:", error);
            message.error("批量备注追加失败: " + (error.message || "未知错误"));
        } finally {
            setBatchRemarksLoading(false);
        }
    }, [displayTransactions, transactionManager, batchRemarksValue]);

    // 表格列定义（对齐旧版）
    const columns = [
        {
            title: "序号",
            key: "index",
            width: 40,
            render: (_, _record, index) => <div style={{ textAlign: "center", fontSize: 12, color: "#666" }}>{index + 1}</div>,
        },
        {
            title: "变更",
            key: "client_status",
            width: 40,
            align: "center",
            render: (_, record) => {
                // 检查事务本身是否有变更
                const hasTransactionChanges = typeof record.id === "number" && record.originalData && (record.status !== record.originalData.status || record.remarks !== record.originalData.remarks);

                // 检查明细是否有变更
                const hasDetailChanges = record.details?.some((detail) => detail.clientStatus !== CLIENT_STATUS.PRISTINE);

                // 确定显示的状态
                let statusText = "-";
                let statusColor = "default";

                if (typeof record.id === "string") {
                    // 新事务
                    statusText = "新";
                    statusColor = "green";
                } else if (hasTransactionChanges || hasDetailChanges) {
                    // 有变更的事务
                    statusText = "改";
                    statusColor = "orange";
                }

                return (
                    // 如果 statusText = "-" 则不显示tag
                    statusText === "-" ? null : (
                        <Tag color={statusColor} size="small">
                            {statusText}
                        </Tag>
                    )
                );
            },
        },
        {
            title: "航班号",
            key: "flight_number",
            width: 80,
            render: (_, record) => {
                // 航班号已经在 enrichedTransactions 中处理过，优先使用快照数据
                const flightNumber = record.flightNumber;
                const isUsingSnapshot = record.hasFlightSnapshot && record.useSnapshotData;

                return (
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontWeight: "bold", fontSize: 14 }}>{flightNumber}</div>
                        {isUsingSnapshot && <div style={{ fontSize: 10, color: "#999", fontStyle: "italic" }}>快照</div>}
                    </div>
                );
            },
        },
        {
            title: "航段",
            key: "route",
            width: 80,
            render: (_, record) => {
                // 航段信息已经在 enrichedTransactions 中处理过，优先使用快照数据
                const route = record.route;

                return <div style={{ textAlign: "center", fontSize: 12 }}>{route}</div>;
            },
        },
        {
            title: "航班时间",
            key: "flight_times",
            width: 240,
            render: (_, record) => {
                // 航班时间信息已经在 enrichedTransactions 中处理过，优先使用快照数据
                // 构建用于 EnhancedFlightTimes 组件的航班数据对象
                const flightData = {
                    ...record,
                    flight_number: record.flightNumber,
                    flight_date: record.flightDate,
                    departure_airport: record.departureAirport,
                    arrival_airport: record.arrivalAirport,
                    aircraft_reg: record.aircraftReg,
                    std: record.std,
                    sta: record.sta,
                    etd: record.etd,
                    eta: record.eta,
                    atd: record.atd,
                    ata: record.ata,
                    status: record.flightStatus, // 航班状态使用实时数据
                    // 添加过站时间相关字段（如果有的话）
                    min_ground_time_minutes: record.min_ground_time_minutes,
                    actual_ground_time_minutes: record.actual_ground_time_minutes,
                };

                return (
                    <div>
                        <EnhancedFlightTimes
                            flight={flightData}
                            airportsMap={airportsMap}
                            allFlights={[]} // 不需要所有航班数据
                            onFlightInfoEditSuccess={() => {}} // 事务中不需要编辑功能
                            editable={false} // 事务中不允许编辑航班时间
                        />
                        {record.hasFlightSnapshot && record.useSnapshotData && <div style={{ fontSize: 10, color: "#999", fontStyle: "italic", marginTop: 2 }}>数据来源: 快照</div>}
                    </div>
                );
            },
        },
        {
            title: "事务状态",
            key: "transaction_status",
            width: 140,
            align: "center",
            render: (_, record) => {
                // 检查是否为历史数据（supplier_id为空）
                // const isHistoricalData = !record.supplierId;

                return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <UnifiedStatusEditor record={record} transactionType={transactionType} isDetail={false} statusOptions={transactionStatusOptions} />
                        {/* {isHistoricalData && (
                            <Tag size="small" color="orange" style={{ fontSize: "10px", lineHeight: "14px" }}>
                                历史数据
                            </Tag>
                        )} */}
                    </div>
                );
            },
        },
        {
            title: "明细",
            key: "details_count",
            width: 60,
            align: "center",
            render: (_, record) => {
                const isExpanded = expandedRowKeys.includes(record.id);
                const transactionChanges = transactions.find((t) => t.id === record.id);
                const detailsCount = transactionChanges?.details?.filter((d) => d.clientStatus !== CLIENT_STATUS.DELETED).length || 0;
                const hasDetails = detailsCount > 0;
                const missingDetails = transactionManager.getTransactionsMissingDetails();
                const isMissingDetails = missingDetails.includes(record.id);

                if (!hasDetails) {
                    return (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Tag
                                color={isMissingDetails ? "red" : "default"}
                                style={{ cursor: record.isNewTransaction ? "pointer" : "default" }}
                                onClick={() => {
                                    handleAddProduct(record);
                                }}
                            >
                                {record.isNewTransaction ? "待选择产品" : "无明细"}
                            </Tag>
                        </div>
                    );
                }

                return (
                    <Button
                        type="text"
                        size="small"
                        icon={isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                        onClick={() => handleDetailsCountClick(record)}
                        style={{
                            color: record.isNewTransaction ? "#fa8c16" : "#52c41a",
                        }}
                    >
                        {record.isNewTransaction ? `${detailsCount} 项待创建` : `${detailsCount} 项`}
                    </Button>
                );
            },
        },
        {
            title: "操作",
            key: "actions",
            width: 60,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="添加产品">
                        <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => handleAddProduct(record)} disabled={!selectedSupplier} />
                    </Tooltip>
                    <Tooltip title="查看航班历史">
                        <Button
                            type={selectedFlightForHistory?.id === record.flightId ? "primary" : "text"}
                            size="small"
                            icon={selectedFlightForHistory?.id === record.flightId ? <RightOutlined /> : <EyeOutlined />}
                            onClick={() => handleViewHistory(record)}
                            style={{
                                color: selectedFlightForHistory?.id === record.flightId ? "#fff" : "#1890ff",
                                backgroundColor: selectedFlightForHistory?.id === record.flightId ? "#1890ff" : "transparent",
                                borderColor: selectedFlightForHistory?.id === record.flightId ? "#1890ff" : "transparent",
                                fontWeight: selectedFlightForHistory?.id === record.flightId ? "bold" : "normal",
                            }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // 如果没有选择供应商，显示提示信息
    if (!selectedSupplier) {
        return (
            <Card title="事务列表" size="small">
                <div
                    style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#999",
                        fontSize: "14px",
                    }}
                >
                    <ShopOutlined style={{ fontSize: "24px", marginBottom: "12px", display: "block" }} />
                    请先选择供应商以查看对应的事务数据
                </div>
            </Card>
        );
    }

    return (
        <Card
            title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "3.8em" }}>
                    <div>
                        <span>事务列表</span>
                        <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>仅显示当前选中的机场和供应商的事务：{selectedSupplier.name}</div>
                    </div>
                    <Space>
                        <Button type="secondary" icon={allExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />} onClick={handleToggleAllExpanded}>
                            {allExpanded ? "收缩" : "展开"}
                        </Button>
                        <Divider type="vertical"></Divider>
                        批量：
                        <Select
                            placeholder="变更状态"
                            allowClear
                            onChange={handleBatchStatusChange}
                            options={transactionStatusOptions.map((opt) => ({
                                value: opt.value,
                                label: (
                                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Tag color={opt.color}>{opt.label}</Tag>
                                    </div>
                                ),
                            }))}
                            style={{ minHeight: "2.5725em" }}
                        />
                        <Button type="default" icon={<EditOutlined />} onClick={handleBatchRemarksClick}>
                            追加备注
                        </Button>
                        <Button type="primary" ghost icon={<ShopOutlined />} onClick={handleBatchEditProducts}>
                            修改产品
                        </Button>
                    </Space>
                </div>
            }
            size="small"
        >
            <Table
                dataSource={displayTransactions}
                columns={columns}
                rowKey="id"
                pagination={false}
                size="small"
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: setExpandedRowKeys,
                    expandRowByClick: false,
                    showExpandColumn: false, // 隐藏默认展开列，使用明细列的按钮控制
                    expandedRowRender: (record) => <TransactionDetailTable transactionId={record.id} transactionType={transactionType} />,
                }}
                loading={orchestrator.transactionListLoading || dataLoadingStatus === "LOADING"}
            />

            {/* 产品选择模态框 */}
            <ProductSelectorModal
                visible={productModalVisible}
                onCancel={() => {
                    setProductModalVisible(false);
                    setCurrentTransactionForProduct(null);
                }}
                onConfirm={handleProductSelect}
                availableItems={modalAvailableProducts}
                selectedFlights={selectedFlights}
                transactionType={transactionType}
                selectedSupplierId={selectedSupplier?.id}
                title={currentTransactionForProduct ? `为事务添加产品` : `批量修改产品`}
            />

            {/* 批量追加备注模态框 */}
            <Modal title="批量追加备注" open={batchRemarksModalVisible} onCancel={handleBatchRemarksCancel} onOk={handleBatchRemarksConfirm} confirmLoading={batchRemarksLoading} okText="确认追加" cancelText="取消" width={500}>
                <div style={{ marginBottom: 16 }}>
                    <p style={{ color: "#666", marginBottom: 12 }}>
                        将为所有事务及其明细追加备注信息（不会覆盖原有备注）。当前共有 <strong>{displayTransactions.length}</strong> 个事务。
                    </p>
                    <Input.TextArea value={batchRemarksValue} onChange={(e) => setBatchRemarksValue(e.target.value)} placeholder="请输入要追加的备注信息..." rows={4} maxLength={500} showCount />
                </div>
            </Modal>
        </Card>
    );
};

export default TransactionTable;
